import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ChevronDown, X } from 'lucide-react';
// ── Credential types ─────────────────────────────────────────────────────────
type CredentialType = 'email' | 'apikey' | 'sql-string' | 'sql-fields';
const CREDENTIAL_TYPES: {
  id: CredentialType;
  label: string;
}[] = [{
  id: 'email',
  label: 'Email & Password'
}, {
  id: 'apikey',
  label: 'API Key'
}, {
  id: 'sql-string',
  label: 'SQL (connection string)'
}, {
  id: 'sql-fields',
  label: 'SQL (fields)'
}];
const TYPE_LABEL: Record<CredentialType, string> = {
  email: 'Email & Password',
  apikey: 'API Key',
  'sql-string': 'SQL (connection string)',
  'sql-fields': 'SQL (fields)'
};
// Shared field shell — label above an input, with optional helper text below.
function Field({
  label,
  children,
  helper
}: {
  label: string;
  children: React.ReactNode;
  helper?: string;
}) {
  return <div className="flex flex-col" data-id="element-1200">
      <label className="text-sm font-semibold text-neutral-900 mb-1.5" data-id="element-1201">
        {label}
      </label>
      {children}
      {helper && <p className="mt-1.5 text-xs text-neutral-400 leading-relaxed" data-id="element-1202">
          {helper}
        </p>}
    </div>;
}
const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-900/5 transition-colors';
// Per-type form body. Each renders only the fields that type needs.
function CredentialFields({
  type
}: {
  type: CredentialType;
}) {
  return <div className="flex flex-col gap-6" data-id="element-1203">
      <Field label="Label" helper="Optional — defaults to the email / host if left blank." data-id="element-1204">
        <input type="text" placeholder={TYPE_LABEL[type]} className={inputClass} data-id="element-1205" />
      </Field>

      {type === 'email' && <>
          <Field label="Website URL" helper="Optional — where this credential is used." data-id="element-1206">
            <input type="url" placeholder="https://example.com" className={inputClass} data-id="element-1207" />
          </Field>
          <Field label="Email or username" data-id="element-1208">
            <input type="text" placeholder="you@example.com" className={inputClass} data-id="element-1209" />
          </Field>
          <Field label="Password" data-id="element-1210">
            <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-1211" />
          </Field>
        </>}

      {type === 'apikey' && <Field label="API key" data-id="element-1212">
          <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-1213" />
        </Field>}

      {type === 'sql-string' && <Field label="Connection string" helper="e.g. postgresql://user:password@host:5432/dbname — parsed into host/port/db on save." data-id="element-1214">
          <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-1215" />
        </Field>}

      {type === 'sql-fields' && <>
          <div className="grid grid-cols-[1fr_120px] gap-3" data-id="element-1216">
            <Field label="Host" data-id="element-1217">
              <input type="text" placeholder="db.example.com" className={inputClass} data-id="element-1218" />
            </Field>
            <Field label="Port" data-id="element-1219">
              <input type="text" placeholder="5432" className={inputClass} data-id="element-1220" />
            </Field>
          </div>
          <Field label="Database" data-id="element-1221">
            <input type="text" placeholder="mydb" className={inputClass} data-id="element-1222" />
          </Field>
          <div className="grid grid-cols-2 gap-3" data-id="element-1223">
            <Field label="User" data-id="element-1224">
              <input type="text" placeholder="user" className={inputClass} data-id="element-1225" />
            </Field>
            <Field label="Password" data-id="element-1226">
              <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-1227" />
            </Field>
          </div>
          <Field label="SSL mode" helper="Optional — e.g. require, verify-full, disable." data-id="element-1228">
            <input type="text" placeholder="require" className={inputClass} data-id="element-1229" />
          </Field>
        </>}

      <Field label="Shared with spaces" helper="Members of these spaces can use this connection." data-id="element-1230">
        <input type="text" placeholder="Search spaces…" className={inputClass} data-id="element-1231" />
      </Field>
    </div>;
}
// Right-hand slide-over panel for creating a new custom credential.
function CredentialPanel({
  type,
  onClose
}: {
  type: CredentialType;
  onClose: () => void;
}) {
  // Close on Escape for keyboard accessibility.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <>
      {/* Scrim */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30" aria-hidden="true" data-id="element-1232" />

      {/* Panel */}
      <motion.aside role="dialog" aria-modal="true" aria-label="New custom credential" initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 34
    }} className="fixed top-0 right-0 z-50 flex h-full w-full max-w-[460px] flex-col bg-white shadow-2xl" data-id="element-1233">
        {/* Header */}
        <div className="flex items-start justify-between px-7 pt-7 pb-5" data-id="element-1234">
          <div data-id="element-1235">
            <p className="text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-1236">
              New connection · {TYPE_LABEL[type]}
            </p>
            <h2 className="mt-1.5 text-xl font-bold text-neutral-900" data-id="element-1237">
              New custom credential
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-neutral-400 hover:text-neutral-700 transition-colors" data-id="element-1238">
            <X size={20} data-id="element-1239" />
          </button>
        </div>

        {/* Scrollable form */}
        <div className="flex-1 overflow-y-auto px-7 pb-7" data-id="element-1240">
          <CredentialFields type={type} data-id="element-1241" />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 border-t border-neutral-100 px-7 py-4" data-id="element-1242">
          <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 transition-colors" data-id="element-1243">
            Cancel
          </button>
          <button onClick={onClose} className="rounded-lg bg-[#2B211A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1A1410] transition-colors" data-id="element-1244">
            Connect
          </button>
        </div>
      </motion.aside>
    </>;
}
export function CustomCredentialFlow() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeType, setActiveType] = useState<CredentialType | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Close the dropdown on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [menuOpen]);
  const handleSelect = (type: CredentialType) => {
    setMenuOpen(false);
    setActiveType(type);
  };
  return <div className="relative" ref={menuRef} data-id="element-1245">
      <button onClick={() => setMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={menuOpen} className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-1246">
        <Plus size={16} data-id="element-1247" />
        Add custom
        <ChevronDown size={14} className={`ml-0.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} data-id="element-1248" />
      </button>

      <AnimatePresence data-id="element-1249">
        {menuOpen && <motion.div role="menu" initial={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute right-0 top-full mt-2 w-64 origin-top-right rounded-xl border border-neutral-200 bg-white py-2 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.2)] z-50" data-id="element-1250">
            <p className="px-4 pt-1 pb-2 text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-1251">
              Credential type
            </p>
            {CREDENTIAL_TYPES.map(t => <button key={t.id} role="menuitem" onClick={() => handleSelect(t.id)} className="block w-full px-4 py-2.5 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors" data-id="element-1252">
                {t.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-1253">
        {activeType && <CredentialPanel type={activeType} onClose={() => setActiveType(null)} data-id="element-1254" />}
      </AnimatePresence>
    </div>;
}